import { workspaceFetch } from '../../../shared/platform/business-workspaces';
/**
 * The 08 asset review panel: the LOCAL_ASSET half of the same 审核中心 page.
 * LOCAL SYNTHETIC / NON-OPERATIONAL. NOT FORMAL-REGISTRY-AUTHORITY.
 *
 * <p>WHY IT IS A SEPARATE PANEL FROM THE PRODUCT QUEUE AND NOT A BRANCH INSIDE IT. The two kinds are
 * reviewed through different Owners and different publication paths: a product publication is
 * requested through the 06 surface, an equipment publication through the 05 one, and the reference a
 * decision is about is built differently for each. One component that branched on the kind would put
 * both wires in one place and make "which Owner am I about to ask" a runtime question. Two panels on
 * one page, each labelled by the kind it shows, keeps each wire single and the navigation shared.
 *
 * <p>WHAT IT SHOWS AND WHAT IT REFUSES TO SHOW. The immutable submitted content is decoded from the
 * exact byte frames the server sent; if it cannot be decoded the panel says so and DISABLES approval,
 * because approving content the reviewer cannot read is not a review. The manufacturer's claim on a
 * linked product is shown as 06's claim, beside the agency's own configuration declaration and never
 * merged with it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiBaseOrigin } from '../../../shared/config/api-origin';
import { ContentChanges, ContentViewer } from '../../../shared/content-media/ContentMedia';
import {
  ReviewSummary,
  priorReview,
  reviewDecisionLabel,
} from '../../../shared/content-media/ReviewSummary';
import { contentFrames } from '../../../shared/content-media/content-media';
import { createLiveProductReviewAdapter } from '../../../shared/product-review/adapters/live-product-review-adapter';
import type {
  ProductReviewPort,
  ReviewCaseView,
  ServerOutcome,
} from '../../../shared/product-review/adapters/product-review-port';
import { SynAssessmentQueue } from '../../capability-equipment/assessment/ui/SynAssessment';
import { createLiveLocalAssetWorkflowAdapter } from '../../capability-equipment/assets/adapters/live-local-asset-workflow-adapter';
import type { LocalAssetWorkflowPort } from '../../capability-equipment/assets/adapters/local-asset-workflow-port';
import './review-center.css';

interface Session {
  readonly session: string;
  readonly csrf: string;
  readonly account: string;
  readonly expiresAt: string;
}

function parseSession(value: unknown): Session {
  if (
    typeof value !== 'object' ||
    value === null ||
    Object.keys(value).sort().join(',') !== 'account,csrf,expiresAt,session'
  ) {
    throw new Error('Invalid session response');
  }
  const fields = value as Record<string, unknown>;
  for (const name of ['session', 'csrf', 'account', 'expiresAt']) {
    if (typeof fields[name] !== 'string' || !fields[name])
      throw new Error('Invalid session response');
  }
  return fields as unknown as Session;
}

/** One decoded declaration. The fields are the 05 canonical order, and none is normalised. */
export interface DecodedDeclaration {
  readonly displayName: string;
  readonly category: string;
  readonly internalRef: string;
  readonly purpose: string;
  readonly configurationDeclaration: string;
  readonly sourceNote: string;
  readonly linkKind: string;
  readonly productId: string;
  readonly publishedVersion: string;
  readonly productDigest: string;
}

/**
 * Decode the 05 canonical UTF-8 byte-length frames without normalising any field.
 *
 * <p>TWELVE FRAMES, AND THE COUNT IS PART OF THE CONTRACT. A decoder that accepted a different
 * number would render content the submitter did not submit — and this content is what an approval is
 * about. A failure to decode returns null, and the caller then refuses to approve.
 */
export function exactAssetDeclaration(review: ReviewCaseView): DecodedDeclaration | null {
  const content = review.submitted.immutableContent;
  if (!content) return null;
  const bytes = new TextEncoder().encode(content);
  const values: string[] = [];
  let offset = 0;
  try {
    const decoder = new TextDecoder('utf-8', { fatal: true });
    for (let index = 0; index < contentFrames(content).length; index += 1) {
      const colon = bytes.indexOf(58, offset);
      if (colon < 0) return null;
      const lengthText = decoder.decode(bytes.slice(offset, colon));
      if (!/^(0|[1-9][0-9]*)$/.test(lengthText)) return null;
      const length = Number(lengthText);
      if (!Number.isSafeInteger(length) || length > bytes.length - colon - 1) return null;
      offset = colon + 1;
      values.push(decoder.decode(bytes.slice(offset, offset + length)));
      offset += length;
    }
    if (
      offset !== bytes.length ||
      !(values.length === 12 || (values.length === 14 && values[12] === 'CA04-CONTENT-V1'))
    )
      return null;
    return {
      displayName: values[0]!,
      category: values[1]!,
      internalRef: values[2]!,
      purpose: values[3]!,
      configurationDeclaration: values[4]!,
      sourceNote: values[5]!,
      linkKind: values[6]!,
      productId: values[7]!,
      publishedVersion: values[10]!,
      productDigest: values[11]!,
    };
  } catch {
    return null;
  }
}

function outcomeText(outcome: ServerOutcome<unknown>): string {
  if (outcome.kind === 'OK') return '服务端已确认';
  if (outcome.kind === 'REFUSED') {
    return `服务端拒绝 HTTP ${outcome.httpStatus} · ${outcome.code}${outcome.field ? ` · ${outcome.field}` : ''}`;
  }
  if (outcome.kind === 'UNREACHABLE') return '结果未确认。请读回；写入可使用原请求与原幂等键重试。';
  return '响应契约异常，结果未确认。请读回或重试原请求。';
}

function newKey(prefix: string): string {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `${prefix}-${random}`;
}

/** The submitted revision as the 05 publication route needs it. */
function assetSubmission(review: ReviewCaseView) {
  return {
    country: review.submitted.country,
    agency: review.submitted.ownerScope,
    revision: review.submitted.revision,
    digest: review.submitted.digest,
  };
}

export function SynLiveAssetReviewQueue() {
  const [auth, setAuth] = useState<Session | null>(null);
  const [credential, setCredential] = useState('');
  const [failure, setFailure] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [cases, setCases] = useState<readonly ReviewCaseView[]>([]);
  const [selected, setSelected] = useState<ReviewCaseView | null>(null);
  const [decisionId, setDecisionId] = useState('');
  const [reason, setReason] = useState('');
  const [readback, setReadback] = useState('');
  // The intake form. There is no auto-discovery contract, exactly as on the product surface: the
  // reviewer transcribes the exact immutable revision the agency's page read back. A queue that
  // "found the latest submission" would be reviewing whatever arrived last rather than the revision
  // the agency actually froze.
  const [intake, setIntake] = useState({
    caseId: '',
    assetId: '',
    revision: '',
    digest: '',
    country: '',
    agency: '',
    reviewScope: '',
  });
  const origin = apiBaseOrigin();

  /**
   * BOTH PORTS ARE MEMOISED ON THE SESSION, AND THAT IS NOT AN OPTIMISATION.
   *
   * <p>A port built on every render would make `refresh` a new function on every render, which
   * makes the effect that calls it re-run on every render, which fetches the queue again — a loop
   * that looks like a slow page and is really an unbounded request cycle. Keying them on the
   * session means they change exactly when the credentials do, which is when they must.
   */
  const reviewPort: ProductReviewPort = useMemo(
    () => createLiveProductReviewAdapter({ baseOrigin: origin, csrf: () => auth?.csrf ?? '' }),
    [auth, origin],
  );
  const assetPort: LocalAssetWorkflowPort = useMemo(
    () => createLiveLocalAssetWorkflowAdapter({ baseOrigin: origin, csrf: () => auth?.csrf ?? '' }),
    [auth, origin],
  );

  const run = useCallback(async (label: string, action: () => Promise<void>) => {
    setBusy(true);
    setNotice(`${label}…`);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }, []);

  async function login() {
    setBusy(true);
    setFailure('');
    try {
      const response = await workspaceFetch(`${origin}/management/product-review/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
        credentials: 'omit',
        redirect: 'error',
        cache: 'no-store',
      });
      if (!response.ok) {
        setFailure(`会话未建立（HTTP ${response.status}）`);
        setAuth(null);
        return;
      }
      setAuth(parseSession(await response.json()));
    } catch (error) {
      setFailure(error instanceof Error ? error.message : '会话请求失败');
      setAuth(null);
    } finally {
      setBusy(false);
    }
  }

  const refresh = useCallback(async () => {
    if (!auth) return;
    setSelected(null);
    setDecisionId('');
    setReason('');
    setReadback('');
    const outcome = await reviewPort.listCases({ session: auth.session });
    if (outcome.kind !== 'OK') {
      setNotice(outcomeText(outcome));
      // A failed read CLEARS the queue. Leaving the previous list up would show the reviewer cases
      // this session may no longer be entitled to see.
      setCases([]);
      setSelected(null);
      setDecisionId('');
      setReason('');
      setReadback('');
      return;
    }
    const available = outcome.value.filter((item) => item.submitted.subjectKind === 'LOCAL_ASSET');
    setCases(available);
  }, [auth, reviewPort]);

  useEffect(() => {
    if (!auth) {
      setCases([]);
      setSelected(null);
      return;
    }
    void refresh();
  }, [auth, refresh]);

  const declaration = selected ? exactAssetDeclaration(selected) : null;
  const prior = priorReview(cases, selected);

  return (
    <section className="portal-card governance-syn-asset" aria-label="08 本地装备审核">
      <div className="portal-card-head">
        <h2>审核队列 · 本地装备（LOCAL_ASSET）</h2>
        <button
          className="portal-button"
          disabled={busy || !auth}
          onClick={() => void run('刷新装备审核队列', refresh)}
        >
          刷新队列
        </button>
      </div>
      <p className="provider-fixture-note">
        本地装备与产品型号是两类主体。队列只展示当前账号获准的 08 记录；空队列不代表已发布。
      </p>
      {!auth ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void login();
          }}
        >
          <label>
            08 合成会话凭据
            <input
              aria-label="08 合成会话凭据"
              value={credential}
              onChange={(event) => setCredential(event.target.value)}
              autoComplete="off"
            />
          </label>
          <button className="portal-button" type="submit" disabled={busy || !credential}>
            建立合成会话
          </button>
          {failure && <p role="status">{failure}</p>}
        </form>
      ) : (
        <>
          <p data-syn-asset-reviewer>
            已登录：{auth.account} · 会话到期 {auth.expiresAt}
          </p>
          <SynAssessmentQueue auth={auth} />
          <details className="cm-audit" aria-label="受理精确提交">
            <summary>受理新的精确提交</summary>
            <h3>受理精确提交</h3>
            <p>
              没有自动发现提交的契约：受理必须抄录机构端读回的精确不可变版本，不接受“最新版本”。
            </p>
            <fieldset disabled={busy}>
              {(
                [
                  ['caseId', '本次受理编号'],
                  ['assetId', '装备资产 ID'],
                  ['revision', '提交修订号'],
                  ['digest', '提交摘要'],
                  ['country', '国家范围'],
                  ['agency', '机构范围'],
                  ['reviewScope', '审核范围'],
                ] as const
              ).map(([field, label]) => (
                <label key={field}>
                  {label}
                  <input
                    aria-label={label}
                    value={intake[field]}
                    onChange={(event) => setIntake({ ...intake, [field]: event.target.value })}
                  />
                </label>
              ))}
              <button
                className="portal-button"
                disabled={
                  busy ||
                  !intake.caseId ||
                  !intake.assetId ||
                  !/^[0-9]+$/.test(intake.revision) ||
                  !intake.digest ||
                  !intake.country ||
                  !intake.agency ||
                  !intake.reviewScope
                }
                onClick={() =>
                  void run('受理精确提交', async () => {
                    const result = await reviewPort.receive({
                      session: auth.session,
                      idempotencyKey: newKey('asset-receive'),
                      caseId: intake.caseId,
                      revision: {
                        subjectKind: 'LOCAL_ASSET',
                        productId: intake.assetId,
                        revision: Number(intake.revision),
                        digest: intake.digest,
                        country: intake.country,
                        supplier: intake.agency,
                        reviewScope: intake.reviewScope,
                      },
                    });
                    setNotice(outcomeText(result));
                    if (result.kind === 'OK') {
                      setCases([
                        ...cases.filter((item) => item.caseId !== result.value.caseId),
                        result.value,
                      ]);
                      setSelected(result.value);
                      setDecisionId('');
                      setReason('');
                      setReadback('');
                    }
                  })
                }
              >
                受理精确提交
              </button>
            </fieldset>
          </details>
          <div className="provider-products-layout cm-review-layout">
            <aside className="provider-product-list" aria-label="本地装备审核队列">
              <div className="provider-list-heading">
                <h3>
                  装备申请 <small>（{cases.length}）</small>
                </h3>
              </div>
              <div className="provider-products-scroll">
                {cases.map((item) => (
                  <button
                    className={`provider-list-product ${item.caseId === selected?.caseId ? 'is-selected' : ''}`}
                    key={item.caseId}
                    data-syn-asset-case={item.caseId}
                    disabled={busy}
                    onClick={() => {
                      setSelected(item);
                      setDecisionId('');
                      setReason('');
                      setNotice('');
                      setReadback('');
                    }}
                  >
                    <div>
                      <strong>
                        {exactAssetDeclaration(item)?.displayName ?? '提交名称不可读取'}
                      </strong>
                      <small>{item.caseId}</small>
                      <span>修订 {item.submitted.revision}</span>
                      <span>{reviewDecisionLabel(item.decision?.outcome)}</span>
                    </div>
                  </button>
                ))}
                {cases.length === 0 && <p>当前账号没有可审核的本地装备申请。</p>}
              </div>
            </aside>
            <div className="provider-product-detail">
              {selected === null ? (
                <p>请选择一条装备申请。</p>
              ) : (
                <>
                  <ReviewSummary
                    review={selected}
                    name={declaration?.displayName ?? '提交名称不可读取'}
                    decisionTarget="asset-review-decision"
                  />
                  <ContentChanges
                    before={prior?.submitted.immutableContent}
                    after={selected.submitted.immutableContent}
                    beforeRevision={prior?.submitted.revision}
                    afterRevision={selected.submitted.revision}
                  />
                  <section className="cm-section" aria-label="不可变审核内容">
                    <h3>装备申报资料</h3>
                    {declaration ? (
                      <dl className="cm-facts">
                        <div>
                          <dt>装备名称</dt>
                          <dd>{declaration.displayName}</dd>
                        </div>
                        <div>
                          <dt>装备类别</dt>
                          <dd>{declaration.category}</dd>
                        </div>
                        <div>
                          <dt>机构内部编号</dt>
                          <dd>{declaration.internalRef || '未填写'}</dd>
                        </div>
                        <div>
                          <dt>用途说明</dt>
                          <dd>{declaration.purpose}</dd>
                        </div>
                        <div>
                          <dt>实际配置声明</dt>
                          <dd>{declaration.configurationDeclaration}</dd>
                        </div>
                        <div>
                          <dt>资料来源备注</dt>
                          <dd>{declaration.sourceNote}</dd>
                        </div>
                        <div>
                          <dt>型号关联</dt>
                          <dd>
                            {declaration.linkKind}
                            {declaration.linkKind === 'LINKED'
                              ? ` · ${declaration.productId} v${declaration.publishedVersion} · ${declaration.productDigest.slice(0, 12)}…`
                              : ''}
                          </dd>
                        </div>
                      </dl>
                    ) : (
                      <p>
                        不可变内容不可用或格式异常，禁止审核通过。请重新读取，不能使用当前可变草稿代替。
                      </p>
                    )}
                    <details className="cm-audit">
                      <summary>查看原始不可变内容与摘要</summary>
                      <p>摘要：{selected.submitted.digest}</p>
                      <pre>{selected.submitted.immutableContent ?? 'UNAVAILABLE'}</pre>
                    </details>
                  </section>
                  <ContentViewer
                    owner="05"
                    auth={{ session: auth?.session ?? '' }}
                    reference={{
                      subjectId: selected.submitted.subjectId,
                      revision: selected.submitted.revision,
                      digest: selected.submitted.digest,
                      view: 'submitted',
                      caseId: selected.caseId,
                    }}
                  />
                  <section className="cm-decision" id="asset-review-decision" tabIndex={-1}>
                    <h4>审核决定</h4>
                    <p>
                      当前结论：{reviewDecisionLabel(selected.decision?.outcome)} · 决定编号{' '}
                      {selected.decision?.decisionId ?? '无'}
                    </p>
                    {selected.decision && <p>审核意见：{selected.decision.reason}</p>}
                    <fieldset disabled={busy || selected.decision !== null}>
                      <label>
                        决定编号
                        <input
                          aria-label="决定编号"
                          value={decisionId}
                          onChange={(event) => setDecisionId(event.target.value)}
                        />
                      </label>
                      <label>
                        审核意见
                        <textarea
                          aria-label="审核意见"
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                        />
                      </label>
                      <div>
                        {(['CHANGES_REQUESTED', 'ACCEPTED'] as const).map((outcome) => (
                          <button
                            className="portal-button"
                            key={outcome}
                            disabled={
                              !decisionId ||
                              !reason ||
                              (outcome === 'ACCEPTED' && declaration === null)
                            }
                            onClick={() =>
                              void run(
                                outcome === 'ACCEPTED' ? '审核通过' : '退回修改',
                                async () => {
                                  const result = await reviewPort.decide({
                                    session: auth.session,
                                    idempotencyKey: newKey('asset-decide'),
                                    caseId: selected.caseId,
                                    revision: {
                                      productId: selected.submitted.subjectId,
                                      revision: selected.submitted.revision,
                                      digest: selected.submitted.digest,
                                      country: selected.submitted.country,
                                      supplier: selected.submitted.ownerScope,
                                      reviewScope: selected.submitted.reviewScope,
                                      subjectKind: 'LOCAL_ASSET',
                                    },
                                    expectedVersion: selected.version,
                                    decisionId,
                                    outcome,
                                    reason,
                                  });
                                  setNotice(outcomeText(result));
                                  if (result.kind === 'OK') {
                                    setSelected(result.value);
                                    setCases(
                                      cases.map((item) =>
                                        item.caseId === result.value.caseId ? result.value : item,
                                      ),
                                    );
                                  }
                                },
                              )
                            }
                          >
                            {outcome === 'ACCEPTED' ? '审核通过' : '退回修改'}
                          </button>
                        ))}
                      </div>
                    </fieldset>
                  </section>
                  <section>
                    <h4>05 Owner 发布与精确读回</h4>
                    <p>
                      审核通过只产生审核决定。发布由 05 Owner
                      以独立机器身份执行；此页面不能把状态直接改成已发布，也不会显示未核验的“已发布”。
                    </p>
                    <div>
                      <button
                        className="portal-button"
                        disabled={busy || selected.decision?.outcome !== 'ACCEPTED'}
                        onClick={() =>
                          void run('请求 05 Owner 发布', async () => {
                            const result = await assetPort.publish({
                              session: auth.session,
                              idempotencyKey: newKey('asset-publish'),
                              assetId: selected.submitted.subjectId,
                              submission: assetSubmission(selected),
                              decisionId: selected.decision!.decisionId,
                            });
                            setNotice(
                              result.kind === 'OK'
                                ? '05 Owner 已确认发布。请点击“核对发布与精确读回”由服务端独立确认。'
                                : outcomeText(result),
                            );
                          })
                        }
                      >
                        请求 05 Owner 发布
                      </button>
                      <button
                        className="portal-button"
                        disabled={busy}
                        onClick={() =>
                          void run('核对发布与精确读回', async () => {
                            const result = await reviewPort.observe({
                              session: auth.session,
                              idempotencyKey: newKey('asset-observe'),
                              caseId: selected.caseId,
                              revision: {
                                productId: selected.submitted.subjectId,
                                revision: selected.submitted.revision,
                                digest: selected.submitted.digest,
                                country: selected.submitted.country,
                                supplier: selected.submitted.ownerScope,
                                reviewScope: selected.submitted.reviewScope,
                                subjectKind: 'LOCAL_ASSET',
                              },
                              expectedVersion: selected.version,
                            });
                            setNotice(outcomeText(result));
                            if (result.kind === 'OK') {
                              setSelected(result.value);
                              const observation = result.value.observation;
                              setReadback(
                                observation === null
                                  ? '服务端未返回观察记录；本次没有确认读回。'
                                  : observation.exactReadbackConfirmed
                                    ? `精确读回已确认 · 发布版本 ${observation.readbackPublishedVersion}`
                                    : `尚未确认 · owner=${observation.ownerStatus} readback=${observation.readbackStatus}`,
                              );
                            }
                          })
                        }
                      >
                        核对发布与精确读回
                      </button>
                    </div>
                    {readback && <p data-syn-asset-readback>{readback}</p>}
                  </section>
                </>
              )}
            </div>
          </div>
        </>
      )}
      {notice && <p role="status">{notice}</p>}
    </section>
  );
}
